import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';
import {EditPermissions, NodeFormBuilder} from '../../../../../../../shared/models';
import {FormControl, FormGroup} from '@angular/forms';
import {getEditTypes} from '../../../delegatorUtils';
import {required} from '../../../../../../../shared/utils';

/**
 * Interface representing the structure of the ChatDesk form data.
 */
export interface ChatDeskForm {
    name: string;
    provider: string;
    sandboxIntegration: string;
    productionIntegration: string;
    department: string;
    next: string;
    nextDescription: string;
    error: string;
    errorDescription: string;
}

export const NODE_TYPE = 'chatdesk';


/**
 * Class representing a ChatDesk node in the flow builder.
 * Extends the NodeFormBuilder to provide specific functionality for ChatDesk nodes.
 */
export class ChatDesk extends NodeFormBuilder<ChatDeskForm> {

    /**
     * Constructor for the ChatDesk class.
     * Initializes the node with the type 'menu' and the specific node type 'chatdesk'.
     */
    constructor() {
        super('menu', NODE_TYPE);
    }

    /**
     * Extracts and returns the ChatDesk form data from the provided step data.
     * @param stepData - The step data containing menu and chatdesk information.
     * @returns The extracted ChatDesk form data.
     */
    getFromData(stepData: { [index: string]: any }): ChatDeskForm {

        const menu = stepData?.menu ? stepData?.menu : {};
        const chatdesk = menu?.chatdesk ? menu?.chatdesk : {};
        const results = menu?.results ? menu?.results : {};

        return {
            name: menu?.name ? menu?.name : '',
            productionIntegration: chatdesk?.productionIntegration ? chatdesk?.productionIntegration : '',
            sandboxIntegration: chatdesk?.sandboxIntegration ? chatdesk?.sandboxIntegration : '',
            department: chatdesk?.department ? chatdesk?.department : '',
            provider: 'Touch',
            next: results?.next?.next ? results?.next?.next : newStepValue,
            nextDescription: results?.next?.description ? results?.next?.description : 'Next',
            error: results?.error?.next ? results?.error?.next : newStepValue,
            errorDescription: results?.error?.description ? results?.error?.description : 'Error',
        };
    }

    /**
     * Builds and returns a reactive form group for the ChatDesk form.
     * @param formData - The ChatDesk form data to populate the form.
     * @param editFlows - A flag indicating whether the form fields are editable.
     * @returns A FormGroup representing the ChatDesk form.
     */
    buildForm(formData: ChatDeskForm, editFlows: boolean): FormGroup {
        return new FormGroup({
            name: new FormControl({
                value: formData.name,
                disabled: !editFlows,
            }, required('Name')),

            productionIntegration: new FormControl({
                value: formData.productionIntegration,
                disabled: !editFlows,
            }, required('Production Integration')),
            sandboxIntegration: new FormControl({
                value: formData.sandboxIntegration,
                disabled: !editFlows,
            }, required('sandbox Integration')),
            department: new FormControl({
                value: formData.department,
                disabled: !editFlows,
            }),
            provider: new FormControl({
                value: formData.provider,
                disabled: !editFlows,
            }, required('Provider')),

            next: new FormControl(formData.next),
            nextDescription: new FormControl(formData.nextDescription),
            error: new FormControl(formData.error),
            errorDescription: new FormControl(formData.errorDescription),
        });
    }


    /**
     * Getter for the node data.
     * @returns An object representing the node data, including menu and metadata.
     */
    get nodeData(): {} {
        return {
            menu: this.menu,
            'type': 'menu',
            metadata: {
                nodeVersion: '1.0.0',
            },
        };
    }

    /**
     * Sets the node data for the ChatDesk node.
     * Updates the menu property with the provided ChatDesk form data.
     * @param nodeData - The ChatDesk form data to set.
     */
    setNodeData(nodeData: ChatDeskForm) {

        const {
            name,
            provider,
            productionIntegration,
            sandboxIntegration,
            department,
            next,
            nextDescription,
            error,
            errorDescription,
        } = nodeData;

        const chatdesk = {
            provider,
            productionIntegration,
            sandboxIntegration,
            department,
        };

        const results = {
            next: {
                next: next,
                description: nextDescription,
            },
            error: {
                next: error,
                description: errorDescription,
            },
        };

        this.menu = {
            ...this.menu,
            name,
            chatdesk,
            results,
        };
    }
}
